import "./AsignarPlanModal.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { ClienteResponse } from "../../../shared/response/terceros/ClienteResponse";
import { AsignarPlanPayload } from "../../../shared/services/api/payload/terceros/AsignarPlanPayload";
import { AsignarPlanService } from "../../services/terceros/AsignarPlanService";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";

/**
 * Modal unificado para asignar/actualizar el plan de un cliente (`AsignarPlanService`, gRPC):
 * toggle Demo (30 días automáticos) | Plan real (fecha + hectáreas, las dos obligatorias).
 */
export class AsignarPlanModal extends BaseSmallModalFormComponent<
  ClienteResponse,
  AsignarPlanPayload,
  AsignarPlanService
> {
  private _isDemo: boolean;
  private _fechaInput!: HTMLInputElement;
  private _hectareasInput!: HTMLInputElement;

  /** Arranca en Demo si el cliente ya es demo o no tiene plan; en Plan real si tiene vencimiento. */
  constructor(
    private readonly _cliente: ClienteResponse,
    private readonly _onSaved: (item: ClienteResponse) => void,
  ) {
    super(new AsignarPlanService());
    this._isDemo = _cliente.isDemoPlataforma || !_cliente.fechaVencimientoPlataforma;
  }

  /** true si el cliente ya tiene algún plan (demo o con vencimiento): define el título del modal. */
  private get _tienePlan(): boolean {
    return this._cliente.isDemoPlataforma || !!this._cliente.fechaVencimientoPlataforma;
  }

  /** Título: "Actualizar plan" si ya tiene uno, "Nuevo plan" si no. */
  protected get title(): string { return this._tienePlan ? "Actualizar plan" : "Nuevo plan"; }
  /** Texto del botón de guardar. */
  protected get saveText(): string { return "Guardar plan"; }
  /** El estado real del botón lo fija `_validate()` en `onAfterRender`. */
  protected get saveInitiallyDisabled(): boolean { return false; }

  /** Toggle Demo/Plan real y los dos paneles; precarga fecha y hectáreas actuales del cliente. */
  protected renderBody(): string {
    const fechaVal = this._cliente.fechaVencimientoPlataforma
      ? new Date(this._cliente.fechaVencimientoPlataforma).toISOString().slice(0, 10)
      : "";
    // 0 = dato faltante (planes viejos): se deja vacío para obligar a cargarlo.
    const hectareas = this._cliente.maximoHectareasPlataforma > 0 ? String(this._cliente.maximoHectareasPlataforma) : "";

    return `
      <div class="ap-seg">
        <button type="button" class="ap-seg-btn ${this._isDemo ? "active" : ""}" data-mode="demo">Demo</button>
        <button type="button" class="ap-seg-btn ${this._isDemo ? "" : "active"}" data-mode="real">Plan real</button>
      </div>

      <div class="ap-panel ap-panel-demo" ${this._isDemo ? "" : "hidden"}>
        <div class="ap-demo-note">
          <i class="ph ph-flask" aria-hidden="true"></i>
          <div>
            <strong>Demo · 30 días</strong>
            <div class="ap-note-sub">Se activa por 30 días, sin límite de hectáreas. La fecha de vencimiento la calcula el sistema.</div>
          </div>
        </div>
      </div>

      <div class="ap-panel ap-panel-real" ${this._isDemo ? "hidden" : ""}>
        <div class="cl-form-fields">
          <div class="cl-form-group">
            <label class="cl-form-label" for="ap-fecha">Fecha de vencimiento <span class="cl-required">*</span></label>
            <input id="ap-fecha" class="sx-input" type="date" value="${fechaVal}" />
          </div>
          <div class="cl-form-group">
            <label class="cl-form-label" for="ap-hectareas">Máx. hectáreas en plataforma <span class="cl-required">*</span></label>
            <input id="ap-hectareas" class="sx-input sx-input--numeric" type="number" min="1" step="1" value="${hectareas}" />
          </div>
        </div>
      </div>
    `;
  }

  /** Toma referencias a los inputs, cablea el toggle y revalida con cada cambio. */
  protected override onAfterRender(el: HTMLDivElement): void {
    this._fechaInput     = el.querySelector<HTMLInputElement>("#ap-fecha")!;
    this._hectareasInput = el.querySelector<HTMLInputElement>("#ap-hectareas")!;

    el.querySelectorAll<HTMLButtonElement>(".ap-seg-btn").forEach(btn => {
      btn.onclick = () => this._setMode(el, btn.dataset.mode === "demo");
    });
    this._fechaInput.addEventListener("input", () => this._validate());
    this._hectareasInput.addEventListener("input", () => this._validate());
    this._validate();
  }

  /** Cambia entre Demo y Plan real: marca el botón activo, muestra su panel y revalida. */
  private _setMode(el: HTMLDivElement, isDemo: boolean): void {
    this._isDemo = isDemo;
    el.querySelectorAll<HTMLButtonElement>(".ap-seg-btn").forEach(b =>
      b.classList.toggle("active", (b.dataset.mode === "demo") === isDemo),
    );
    el.querySelector<HTMLElement>(".ap-panel-demo")!.toggleAttribute("hidden", !isDemo);
    el.querySelector<HTMLElement>(".ap-panel-real")!.toggleAttribute("hidden", isDemo);
    this._validate();
  }

  /** Demo: solo `isDemo`. Plan real: fecha ISO y hectáreas enteras (ya validadas > 0). */
  protected buildPayload(): AsignarPlanPayload {
    if (this._isDemo) {
      return { id: null, clienteId: this._cliente.id, isDemo: true, fechaVencimiento: null, maximoHectareas: null };
    }
    const fechaRaw = this._fechaInput.value;
    return {
      id: null,
      clienteId: this._cliente.id,
      isDemo: false,
      fechaVencimiento: fechaRaw ? new Date(fechaRaw).toISOString() : null,
      maximoHectareas: this._hectareas(),
    };
  }

  /** Toast de éxito, avisa a la página con el cliente actualizado y cierra el modal. */
  protected override onSaved(item: ClienteResponse): void {
    errorMessageComponent.addSuccess(
      "Plan asignado",
      "Si el cliente no tenía acceso a la plataforma, se le enviará un correo con sus credenciales.",
    );
    this._onSaved(item);
    this.dispose();
  }

  /** Hectáreas del input como entero; 0 si está vacío o no es un número. */
  private _hectareas(): number {
    return Math.floor(Number(this._hectareasInput?.value) || 0);
  }

  /** Demo siempre válido; Plan real requiere fecha de vencimiento y hectáreas > 0. */
  private _validate(): void {
    this.setSaveDisabled(!this._isDemo && (!this._fechaInput?.value || this._hectareas() <= 0));
  }
}
